import React from 'react';
import { cn, formatINR, formatPercent } from '@/lib/utils';

interface PnlValueProps {
  value: number | null | undefined;
  percent?: number | null | undefined;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showSign?: boolean;
  showPercent?: boolean;
  layout?: 'inline' | 'stacked';
  align?: 'left' | 'right' | 'center';
  nullPlaceholder?: string;
  className?: string;
}

export const PnlValue: React.FC<PnlValueProps> = ({
  value,
  percent,
  size = 'md',
  showSign = true,
  showPercent = false,
  layout = 'inline',
  align = 'right',
  nullPlaceholder = 'Pending Cycle',
  className,
}) => {
  if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
    return (
      <span
        className={cn(
          'font-mono text-amber-400/90 bg-amber-950/40 px-2 py-0.5 rounded text-xs border border-amber-900/50 inline-flex items-center gap-1',
          className
        )}
        role="status"
        aria-label={`Market status: ${nullPlaceholder}`}
      >
        <span aria-hidden="true">⏳</span>
        <span>{nullPlaceholder}</span>
      </span>
    );
  }

  const isZero = Object.is(value, 0) || Object.is(value, -0) || Math.abs(value) < 1e-9;
  const isPositive = !isZero && value > 0;
  const isNegative = !isZero && value < 0;

  const colorClass = isPositive
    ? 'text-emerald-400'
    : isNegative
    ? 'text-rose-400'
    : 'text-slate-400';

  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm font-medium',
    lg: 'text-base font-semibold',
    xl: 'text-2xl font-bold',
  };

  const formattedAmount = formatINR(value, { showSign, precision: 2 });
  const isPercentValid =
    percent !== undefined &&
    percent !== null &&
    !isNaN(percent) &&
    isFinite(percent);
  const formattedPercent = isPercentValid
    ? formatPercent(percent, { showSign: true, precision: 2 })
    : null;

  const accessibleLabel = isPositive
    ? `Profit of ${formattedAmount}${formattedPercent ? ` (${formattedPercent})` : ''}`
    : isNegative
    ? `Loss of ${formattedAmount}${formattedPercent ? ` (${formattedPercent})` : ''}`
    : `Neutral P&L ${formattedAmount}${formattedPercent ? ` (${formattedPercent})` : ''}`;

  if (layout === 'stacked') {
    const alignClass =
      align === 'left'
        ? 'items-start text-left'
        : align === 'center'
        ? 'items-center text-center'
        : 'items-end text-right';

    return (
      <div
        className={cn(
          'font-mono tabular-nums inline-flex flex-col transition-colors duration-200 ease-out',
          alignClass,
          colorClass,
          className
        )}
        role="status"
        aria-label={accessibleLabel}
      >
        <span className={cn(sizeClasses[size], 'leading-tight font-medium')}>{formattedAmount}</span>
        {showPercent && (
          <span className="text-[11px] leading-tight font-medium opacity-85 mt-0.5">
            {formattedPercent ?? '—'}
          </span>
        )}
      </div>
    );
  }

  return (
    <span
      className={cn(
        'font-mono tabular-nums inline-flex items-baseline gap-1.5 transition-colors duration-200 ease-out',
        sizeClasses[size],
        colorClass,
        className
      )}
      role="status"
      aria-label={accessibleLabel}
    >
      <span>{formattedAmount}</span>
      {showPercent && formattedPercent && (
        <span className="text-[0.8em] opacity-80 font-mono">
          ({formattedPercent})
        </span>
      )}
    </span>
  );
};
